import React, { useState, useRef, useEffect } from 'react';
import { BuildingStorefrontIcon, MagnifyingGlassIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useFarm } from '../contexts/FarmContext';

const FarmSelector = ({ className = '' }) => {
  const { 
    farms, 
    selectedFarmId, 
    setSelectedFarmId, 
    loading,
    hasFarms 
  } = useFarm();

  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Handle farm selection
  const handleSelect = (farmId) => {
    const parsedFarmId = farmId === 'all' ? 'all' : parseInt(farmId);
    setSelectedFarmId(parsedFarmId);
    setIsOpen(false);
    setSearchQuery('');
    console.log('🏡 Farm selected:', parsedFarmId === 'all' ? 'All Farms' : farms.find(f => f.farm_id === parsedFarmId)?.farm_name);
  };

  // Filter farms based on search
  const filteredFarms = farms.filter(farm => 
    farm.farm_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get selected farm details
  const getSelectedFarmLabel = () => {
    if (selectedFarmId === 'all') {
      return `📊 All Farms (${farms.length})`;
    }
    const selectedFarm = farms.find(f => f.farm_id === selectedFarmId);
    return selectedFarm ? `🏡 ${selectedFarm.farm_name || 'Unnamed Farm'}` : 'Select Farm';
  };

  if (loading) {
    return (
      <div className={`inline-block ${className}`}>
        <select className="input w-64" disabled>
          <option>Loading farms...</option>
        </select>
      </div>
    );
  }

  if (!hasFarms) {
    return (
      <div className={`inline-block ${className}`}>
        <select className="input w-64" disabled>
          <option>No farms available</option>
        </select>
      </div>
    );
  }

  return (
    <div className={`relative inline-block ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="input pl-10 pr-10 w-64 text-left cursor-pointer flex items-center justify-between"
      >
        <BuildingStorefrontIcon className="h-5 w-5 absolute left-3 text-gray-400" />
        <span className="truncate">{getSelectedFarmLabel()}</span>
        <ChevronDownIcon className="h-5 w-5 absolute right-3 text-gray-400" />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-64 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200">
            <div className="relative">
              <MagnifyingGlassIcon className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search farms..."
                className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="max-h-80 overflow-y-auto">
            {/* All Farms Option */}
            <div
              onClick={() => handleSelect('all')}
              className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                selectedFarmId === 'all' ? 'bg-primary-50 text-primary-600' : ''
              }`}
            >
              <span>📊</span>
              <span className="font-medium">All Farms ({farms.length})</span>
            </div>

            {/* Filtered Farms */}
            {filteredFarms.length > 0 ? (
              filteredFarms.map((farm) => (
                <div
                  key={farm.farm_id}
                  onClick={() => handleSelect(farm.farm_id)}
                  className={`px-4 py-2 cursor-pointer hover:bg-gray-100 flex items-center gap-2 ${
                    selectedFarmId === farm.farm_id ? 'bg-primary-50 text-primary-600' : ''
                  }`}
                >
                  <span>🏡</span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{farm.farm_name || 'Unnamed Farm'}</div>
                    <div className="text-xs text-gray-500">{farm.role || 'member'}</div>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-6 text-center text-gray-500 text-sm">
                No farms found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default FarmSelector;
